import { useState, useRef } from "react";
import "@/App.css";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { 
  Phone, 
  MapPin, 
  Clock, 
  ShieldCheck, 
  Users, 
  MessageCircle,
  Heart,
  Check,
  Beef,
  Wine,
  Croissant,
  Apple,
  ChevronRight
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Logo URL
const LOGO_URL = "https://customer-assets.emergentagent.com/job_frutas-ofertas/artifacts/i531c19i_LOGO%20HORTIFRUTI%20IRM%C3%83OS%20REIS.jpg";

// Header Component with Real Logo
const Header = ({ scrollToForm }) => {
  return (
    <header className="bg-white shadow-sm sticky top-0 z-50">
      <div className="max-w-6xl mx-auto px-4 py-2 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img 
            src={LOGO_URL} 
            alt="Hortifruti Irmãos Reis" 
            className="h-16 md:h-20 w-auto object-contain"
            data-testid="header-logo"
          />
        </div>
        <button
          data-testid="header-cta-btn"
          onClick={scrollToForm}
          className="cta-button text-base px-6 py-3"
        >
          Fale Conosco
        </button>
      </div>
    </header>
  );
};

// Hero Section
const HeroSection = ({ scrollToForm, scrollToCategories }) => {
  return (
    <section className="hero-section py-16 md:py-24">
      <div className="max-w-6xl mx-auto px-4">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div className="animate-fade-in-up">
            <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-[#1a1a1a] leading-tight mb-6">
              Produtos Frescos Todo Dia, Perto de Você
            </h2>
            <p className="text-xl md:text-2xl text-gray-700 mb-8 leading-relaxed">
              O Hortifruti Irmãos Reis oferece as melhores frutas, verduras, carnes e muito mais 
              com preços que cabem no seu bolso.
            </p>
            <div className="button-group">
              <button
                data-testid="hero-conheca-btn"
                onClick={scrollToCategories}
                className="cta-button"
              >
                Conheça a Loja
              </button>
              <button
                data-testid="hero-fale-conosco-btn"
                onClick={scrollToForm}
                className="cta-button cta-button-secondary"
              >
                <MessageCircle className="w-5 h-5" />
                Fale Conosco
              </button>
            </div>
          </div>
          <div className="animate-fade-in-up animate-delay-200">
            <img
              src="https://images.unsplash.com/photo-1542838132-92c53300491e?w=600"
              alt="Frutas e verduras frescas"
              className="rounded-3xl shadow-2xl w-full h-auto"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

// Category Card Component
const CategoryCard = ({ image, title, description }) => {
  return (
    <div className="category-card" data-testid={`category-card-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="category-image-container">
        <img
          src={image}
          alt={title}
          className="category-image"
        />
      </div>
      <div className="mt-4">
        <h3 className="category-title">{title}</h3>
        <p className="category-description">{description}</p>
      </div>
    </div>
  );
};

// What You Find Here Section - New institutional section
const WhatYouFindSection = ({ scrollToForm }) => {
  const categories = [
    {
      image: "https://images.unsplash.com/photo-1610832958506-aa56368176cf?w=500",
      title: "Hortifruti Fresco",
      description: "Frutas, verduras e legumes frescos todos os dias."
    },
    {
      image: "https://images.unsplash.com/photo-1607623814075-e51df1bdc82f?w=500",
      title: "Açougue Selecionado",
      description: "Carnes de qualidade com cortes especiais."
    },
    {
      image: "https://images.unsplash.com/photo-1509440159596-0249088772ff?w=500",
      title: "Padaria Artesanal",
      description: "Pães frescos e doces feitos diariamente."
    },
    {
      image: "https://images.unsplash.com/photo-1516594915697-87eb3b1c14ea?w=500",
      title: "Adega com Rótulos Especiais",
      description: "Vinhos selecionados para todas as ocasiões."
    }
  ];

  const benefits = [
    { icon: Apple, text: "Hortifruti fresco" },
    { icon: Beef, text: "Açougue selecionado" },
    { icon: Croissant, text: "Padaria" },
    { icon: Wine, text: "Adega" },
    { icon: Check, text: "Bons preços" },
    { icon: ShieldCheck, text: "Atendimento de confiança" },
  ];

  return (
    <section id="categories" className="py-16 md:py-24 bg-[#f2fff6]">
      <div className="max-w-6xl mx-auto px-4">
        {/* Title */}
        <div className="text-center mb-12">
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-[#009946] mb-4">
            O que você encontra aqui
          </h2>
          <p className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto">
            Selecionamos produtos com cuidado para garantir qualidade no seu dia a dia.
          </p>
        </div>

        {/* Category Cards Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {categories.map((category, index) => (
            <CategoryCard
              key={index}
              image={category.image}
              title={category.title}
              description={category.description}
            />
          ))}
        </div>

        {/* Benefits Strip */}
        <div className="benefits-strip mb-10">
          {benefits.map((benefit, index) => (
            <div key={index} className="benefit-item">
              <Check className="w-5 h-5 text-[#009946]" />
              <span>{benefit.text}</span>
            </div>
          ))}
        </div>

        {/* Main CTA Button */}
        <div className="text-center">
          <button
            data-testid="categories-fale-conosco-btn"
            onClick={scrollToForm}
            className="cta-button-highlight"
          >
            Falar com a Loja
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    </section>
  );
};

// About Section - Who is Hortifruti Irmãos Reis
const AboutSection = ({ scrollToForm }) => {
  const features = [
    { icon: ShieldCheck, title: "Confiança", desc: "Tradição e qualidade" },
    { icon: Apple, title: "Frescor", desc: "Produtos selecionados" },
    { icon: Users, title: "Atendimento", desc: "Próximo e humano" },
    { icon: Heart, title: "Qualidade", desc: "Os melhores produtos" },
  ];

  return (
    <section id="about" className="py-16 md:py-24 bg-white">
      <div className="max-w-6xl mx-auto px-4">
        <div className="about-section-grid">
          <div className="about-image-container">
            <img
              src="https://images.unsplash.com/photo-1488459716781-31db52582fe9?w=800"
              alt="Nossa loja"
              className="about-image"
            />
          </div>
          <div className="about-text-container">
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-[#1a1a1a] mb-6">
              Quem é o Hortifruti Irmãos Reis?
            </h2>
            <p className="text-lg md:text-xl text-gray-700 mb-6 leading-relaxed">
              Somos um hortifruti tradicional de bairro, com raízes na comunidade local. 
              Trabalhamos para levar à sua mesa os 
              <strong className="text-[#009946]"> melhores produtos frescos</strong> com 
              preços justos e atendimento de verdade.
            </p>
            <p className="text-lg md:text-xl text-gray-700 mb-8 leading-relaxed">
              Aqui você encontra qualidade, variedade e um atendimento próximo. 
              Venha nos conhecer e descubra por que tantas famílias confiam no 
              Hortifruti Irmãos Reis.
            </p>
            <div className="grid grid-cols-2 gap-4 mb-8">
              {features.map((feature, index) => (
                <div key={index} className="trust-badge">
                  <feature.icon className="w-8 h-8 text-[#009946]" />
                  <div>
                    <h4 className="font-bold text-[#1a1a1a]">{feature.title}</h4>
                    <p className="text-sm text-gray-600">{feature.desc}</p>
                  </div>
                </div>
              ))}
            </div>
            <button
              data-testid="about-fale-conosco-btn"
              onClick={scrollToForm}
              className="cta-button"
            >
              <MessageCircle className="w-5 h-5" />
              Fale Conosco
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

// Contact Invitation Section
const ContactInviteSection = ({ scrollToForm }) => {
  const benefits = [
    "Ofertas atualizadas frequentemente",
    "Produtos frescos e selecionados",
    "Atendimento próximo e humano",
    "Tire suas dúvidas rapidamente",
    "Saiba sobre disponibilidade de produtos",
  ];

  return (
    <section className="contact-section py-16 md:py-24">
      <div className="max-w-6xl mx-auto px-4">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-[#1a1a1a] mb-6">
              Quer Saber Mais?
            </h2>
            <p className="text-lg md:text-xl text-gray-700 mb-6 leading-relaxed">
              Fale diretamente com a gente! Nossa equipe está pronta para te 
              atender, tirar dúvidas e informar sobre nossos produtos.
            </p>
            <ul className="space-y-4 mb-8">
              {benefits.map((benefit, index) => (
                <li key={index} className="flex items-center gap-3 text-lg text-gray-700">
                  <Check className="w-6 h-6 text-[#009946] flex-shrink-0" />
                  {benefit}
                </li>
              ))}
            </ul>
            <div className="button-group">
              <button
                data-testid="invite-fale-conosco-btn"
                onClick={scrollToForm}
                className="cta-button"
              >
                <MessageCircle className="w-5 h-5" />
                Fale Conosco
              </button>
              <button
                data-testid="invite-tirar-duvidas-btn"
                onClick={scrollToForm}
                className="cta-button cta-button-secondary"
              >
                Tirar Dúvidas
              </button>
            </div>
          </div>
          <div>
            <img
              src="https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=600"
              alt="Atendimento ao cliente"
              className="rounded-3xl shadow-xl w-full"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

// Contact Form Section
const ContactFormSection = ({ formRef }) => {
  const [formData, setFormData] = useState({
    nome: "",
    email: "",
    telefone: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.nome || !formData.email || !formData.telefone) {
      toast.error("Por favor, preencha todos os campos.", {
        duration: 5000,
        style: { fontSize: '18px' }
      });
      return;
    }

    setIsSubmitting(true);

    try {
      await axios.post(`${API}/leads`, formData);
      setSubmitted(true);
      toast.success("Mensagem enviada com sucesso! Entraremos em contato em breve.");
      setFormData({ nome: "", email: "", telefone: "" });
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Erro ao enviar mensagem. Tente novamente.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact-form" ref={formRef} className="py-16 md:py-24 bg-white">
      <div className="max-w-4xl mx-auto px-4">
        <div className="text-center mb-12">
          <span className="inline-block bg-[#009946] text-white px-4 py-2 rounded-full text-lg font-semibold mb-4">
            Entre em Contato
          </span>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-[#1a1a1a] mb-4">
            Fale Conosco
          </h2>
          <p className="text-lg md:text-xl text-gray-600 max-w-xl mx-auto">
            Preencha o formulário abaixo e nossa equipe entrará em contato com você rapidamente
          </p>
        </div>

        {submitted ? (
          <div className="bg-[#e6f7ed] rounded-3xl p-8 md:p-12 text-center">
            <div className="w-20 h-20 bg-[#009946] rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="w-10 h-10 text-white" />
            </div>
            <h3 className="font-heading text-2xl md:text-3xl font-bold text-[#1a1a1a] mb-4">
              Mensagem Enviada!
            </h3>
            <p className="text-lg text-gray-700 mb-6">
              Obrigado pelo seu contato. Nossa equipe entrará em contato com você em breve.
            </p>
            <button
              data-testid="form-new-message-btn"
              onClick={() => setSubmitted(false)}
              className="cta-button"
            >
              Nova Mensagem
            </button>
          </div>
        ) : (
          <form 
            onSubmit={handleSubmit} 
            className="bg-[#f2fff6] rounded-3xl p-8 md:p-12 shadow-lg"
            data-testid="contact-form"
          >
            <div className="space-y-6">
              <div>
                <label htmlFor="nome" className="form-label">
                  Seu Nome
                </label>
                <input
                  type="text"
                  id="nome"
                  name="nome"
                  value={formData.nome}
                  onChange={handleChange}
                  placeholder="Digite seu nome completo"
                  className="form-input"
                  data-testid="form-nome-input"
                />
              </div>
              <div>
                <label htmlFor="email" className="form-label">
                  Seu E-mail
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Digite seu e-mail"
                  className="form-input"
                  data-testid="form-email-input"
                />
              </div>
              <div>
                <label htmlFor="telefone" className="form-label">
                  Seu Telefone (WhatsApp)
                </label>
                <input
                  type="tel"
                  id="telefone"
                  name="telefone"
                  value={formData.telefone}
                  onChange={handleChange}
                  placeholder="(00) 00000-0000"
                  className="form-input"
                  data-testid="form-telefone-input"
                />
              </div>
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full cta-button py-4"
                data-testid="form-submit-btn"
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center gap-3">
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Enviando...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-3">
                    <MessageCircle className="w-5 h-5" />
                    Falar com a Loja
                  </span>
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </section>
  );
};

// Location Section
const LocationSection = ({ scrollToForm }) => {
  const address = "Avenida Matarazzo, 1000 - Centro";
  const mapSrc = "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3657.1975889669026!2d-46.65390068502165!3d-23.565734284683876!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x94ce59c8da0aa315%3A0x63c6e855f3e7c8eb!2sAv.%20Matarazzo%20-%20S%C3%A3o%20Paulo%2C%20SP!5e0!3m2!1spt-BR!2sbr!4v1650000000000!5m2!1spt-BR!2sbr";

  return (
    <section id="location" className="py-16 md:py-24 bg-[#f2fff6]">
      <div className="max-w-6xl mx-auto px-4">
        <div className="text-center mb-12">
          <span className="inline-block bg-[#009946] text-white px-4 py-2 rounded-full text-lg font-semibold mb-4">
            Visite Nossa Loja
          </span>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-[#1a1a1a] mb-4">
            Como Chegar
          </h2>
          <p className="text-lg md:text-xl text-gray-600 max-w-xl mx-auto">
            Venha nos visitar! Estamos esperando você com os melhores produtos e atendimento
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 items-start">
          <div className="bg-white rounded-3xl p-8 shadow-lg">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-14 h-14 bg-[#009946] rounded-full flex items-center justify-center flex-shrink-0">
                <MapPin className="w-7 h-7 text-white" />
              </div>
              <div>
                <h3 className="font-heading text-xl font-bold text-[#1a1a1a] mb-2">
                  Endereço
                </h3>
                <p className="text-lg text-gray-700">{address}</p>
              </div>
            </div>
            <div className="flex items-start gap-4 mb-6">
              <div className="w-14 h-14 bg-[#009946] rounded-full flex items-center justify-center flex-shrink-0">
                <Clock className="w-7 h-7 text-white" />
              </div>
              <div>
                <h3 className="font-heading text-xl font-bold text-[#1a1a1a] mb-2">
                  Horário de Funcionamento
                </h3>
                <p className="text-lg text-gray-700">Segunda a Sábado: 7h às 20h</p>
                <p className="text-lg text-gray-700">Domingo: 7h às 14h</p>
              </div>
            </div>
            <div className="flex items-start gap-4 mb-8">
              <div className="w-14 h-14 bg-[#009946] rounded-full flex items-center justify-center flex-shrink-0">
                <Phone className="w-7 h-7 text-white" />
              </div>
              <div>
                <h3 className="font-heading text-xl font-bold text-[#1a1a1a] mb-2">
                  Telefone
                </h3>
                <p className="text-lg text-gray-700">(11) 99999-9999</p>
              </div>
            </div>
            <button
              data-testid="location-fale-conosco-btn"
              onClick={scrollToForm}
              className="w-full cta-button"
            >
              <MessageCircle className="w-5 h-5" />
              Fale Conosco
            </button>
          </div>
          <div className="map-container">
            <iframe
              src={mapSrc}
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Localização do Hortifruti Irmãos Reis"
            ></iframe>
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer with Logo
const Footer = ({ scrollToForm }) => {
  return (
    <footer className="footer py-12">
      <div className="max-w-6xl mx-auto px-4">
        <div className="grid md:grid-cols-3 gap-8 mb-8">
          <div>
            <div className="mb-4">
              <img 
                src={LOGO_URL} 
                alt="Hortifruti Irmãos Reis" 
                className="h-20 w-auto object-contain bg-white rounded-lg p-2"
                data-testid="footer-logo"
              />
            </div>
            <p className="text-gray-400 text-lg">
              Tradição e qualidade levando frescor para sua mesa.
            </p>
          </div>
          <div>
            <h4 className="font-bold text-white text-lg mb-4">Contato</h4>
            <p className="text-gray-400 mb-2">Avenida Matarazzo, 1000</p>
            <p className="text-gray-400 mb-2">(11) 99999-9999</p>
            <p className="text-gray-400">contato@hortifrutiirmaosreis.com.br</p>
          </div>
          <div>
            <h4 className="font-bold text-white text-lg mb-4">Horário</h4>
            <p className="text-gray-400 mb-2">Segunda a Sábado: 7h às 20h</p>
            <p className="text-gray-400">Domingo: 7h às 14h</p>
          </div>
        </div>
        <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-gray-500 text-center md:text-left">
            © 2024 Hortifruti Irmãos Reis. Todos os direitos reservados.
          </p>
          <button
            data-testid="footer-fale-conosco-btn"
            onClick={scrollToForm}
            className="cta-button"
          >
            <MessageCircle className="w-5 h-5" />
            Fale Conosco
          </button>
        </div>
      </div>
    </footer>
  );
};

// Main App Component - Updated order
function App() {
  const formRef = useRef(null);

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const scrollToCategories = () => {
    const element = document.getElementById("categories");
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      window.scrollTo({ top: elementPosition - offset, behavior: "smooth" });
    }
  };

  return (
    <div className="App bg-[#f2fff6]">
      <Toaster position="top-center" richColors />
      {/* 1. Header with logo */}
      <Header scrollToForm={scrollToForm} />
      {/* 2. Hero / Opening */}
      <HeroSection scrollToForm={scrollToForm} scrollToCategories={scrollToCategories} />
      {/* 3. What you find here (NEW - categories section) */}
      <WhatYouFindSection scrollToForm={scrollToForm} />
      {/* 4. About section */}
      <AboutSection scrollToForm={scrollToForm} />
      {/* 5. Contact invitation */}
      <ContactInviteSection scrollToForm={scrollToForm} />
      {/* 6. Contact form */}
      <ContactFormSection formRef={formRef} />
      {/* 7. How to get there / Address */}
      <LocationSection scrollToForm={scrollToForm} />
      {/* 8. Footer with logo */}
      <Footer scrollToForm={scrollToForm} />
    </div>
  );
}

export default App;
